import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import { FlaskConical, BookOpen, Users, Handshake } from "lucide-react";
import { getSiteConfig } from "@/lib/api";
import api from "@/lib/api";

const LOGO_FALLBACK = "https://customer-assets.emergentagent.com/job_df2c336a-cf24-4860-adef-adda9eebb723/artifacts/c9aru5vi_Color%20Logo.png";

export default function HeroSection() {
  const { ref, inView } = useInView({ threshold: 0.3, triggerOnce: true });
  const [cfg, setCfg] = useState(null);
  const [autoStats, setAutoStats] = useState(null);

  useEffect(() => {
    getSiteConfig("hero").then(r => setCfg(r.data?.data || {})).catch(() => {});
    api.get("/stats/auto").then(r => setAutoStats(r.data)).catch(() => {});
  }, []);

  const title = cfg?.title || "UR-EBioReZ";
  const subtitle = cfg?.subtitle || "Unité de Recherche et d'Innovation en Élevage Biologique et Gestion Durable des Ressources Zoogénétiques";
  const btn1Text = cfg?.btn1_text || "Découvrir nos recherches";
  const btn2Text = cfg?.btn2_text || "Nous contacter";
  const logoUrl = cfg?.logo_url || LOGO_FALLBACK;

  const statItems = [
    { icon: FlaskConical, label: "Projets", value: autoStats?.projects_count || 0 },
    { icon: BookOpen, label: "Publications", value: autoStats?.publications_count || 0 },
    { icon: Users, label: "Chercheurs", value: autoStats?.researchers_count || 0 },
    { icon: Handshake, label: "Partenaires", value: autoStats?.partners_count || 0 },
  ];

  const scrollTo = (id) => {
    const el = document.getElementById(id?.replace("#", ""));
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#1670B9] via-[#2d8fd4] to-[#74CDD9]" />
      {cfg?.bg_image && <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${cfg.bg_image})` }} />}
      <div className="absolute inset-0 opacity-[0.07]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }} />
      <div className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#74CDD9]/10 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 text-center">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <h1 className="font-['Quicksand'] text-5xl sm:text-6xl lg:text-8xl font-bold tracking-tight mb-6" data-testid="hero-title">
            <span style={{color: '#57D0D9'}}>UR</span>
            <span className="text-white/40">-</span>
            <span style={{color: '#0F62B3'}}>E</span>
            <span style={{color: '#0F62B3'}}>B</span>
            <span className="relative" style={{color: '#0F62B3'}}>i<span className="absolute -top-1.5 sm:-top-2 left-1/2 -translate-x-1/2 w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 rounded-full" style={{backgroundColor: '#F47A00'}}></span></span>
            <span style={{color: '#0F62B3'}}>o</span>
            <span style={{color: '#0F62B3'}}>R</span>
            <span style={{color: '#0F62B3'}}>e</span>
            <span style={{color: '#0F62B3'}}>Z</span>
          </h1>
          <p className="text-base md:text-lg text-white/90 max-w-3xl mx-auto leading-relaxed mb-10 font-['Raleway']" data-testid="hero-subtitle">
            {subtitle}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button onClick={() => scrollTo(cfg?.btn1_link || "research")} className="px-8 py-3.5 rounded-xl bg-white text-[#1670B9] font-semibold text-sm hover:bg-white/90 transition-all hover:-translate-y-0.5 shadow-lg hover:shadow-xl" data-testid="hero-cta-research">
              {btn1Text}
            </button>
            <button onClick={() => scrollTo(cfg?.btn2_link || "contact")} className="px-8 py-3.5 rounded-xl border-2 border-white/40 text-white font-semibold text-sm hover:bg-white/10 transition-all hover:-translate-y-0.5" data-testid="hero-cta-contact">
              {btn2Text}
            </button>
          </div>
        </motion.div>

        <div ref={ref} className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-4xl mx-auto">
          {statItems.map((stat, i) => (
            <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: i * 0.1 }}
              className="bg-white/10 backdrop-blur-md rounded-xl p-5 border border-white/20" data-testid={`stat-${stat.label.toLowerCase()}`}>
              <stat.icon className="w-6 h-6 text-[#F49C03] mx-auto mb-2" />
              <div className="text-3xl font-bold text-white font-['Quicksand']">{inView ? <CountUp end={stat.value} duration={2.5} /> : 0}</div>
              <div className="text-xs text-white/70 mt-1 uppercase tracking-wider">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,64C960,75,1056,85,1152,80C1248,75,1344,53,1392,42.7L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z" fill="#FAFAFA"/>
        </svg>
      </div>
    </section>
  );
}
